/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * ProfileDrawer — drawer tài khoản.
 * Badge/admin dùng getPhanHeFlags(profile, useGetPhanHe()) thay vì isAdmin global cũ.
 */
import { Avatar, Button, Drawer, Form, message, Modal, Switch, Tag } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import {
  HEADER_SCROLL_HIDE_STORAGE_KEY,
  headerScrollHideEnabledState,
} from '@/states/headerPreference';
import { useAuthStore } from '@/store/useAuthStore';
import CommonJs from '@/utils/commonJs';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Close, Logout } from '../Icons/IconMenu';
import { LanguageIcon, ShieldIcon } from '../Icons/IconFunc';
import { authApi } from '@/services/authen';
import type { ChangePasswordPayload } from '@/types/authen';
import { ChangePassword, ChangePasswordFormValues } from './ChangePassword';
import { ChangeLanguage, ChangeLanguageFormValues } from './ChangeLanguage';
import { useAppLocale } from '@/contexts/AppLocaleContext';
import { KeyOutlined, PicCenterOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { useGetPhanHe } from '@/hooks/useGetPhanHe';
import { getPhanHeFlags } from '@/utils/phanHeRoles';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    handleLogout: () => void;
}

export const ProfileDrawer: React.FC<Props> = ({ isOpen, onClose, handleLogout }) => {
    const { profile, email } = useAuthStore();
    const { t } = useTranslation();
    const activePhanHe = useGetPhanHe();
    const phanHeFlags = getPhanHeFlags(profile, activePhanHe);
    const { currentLanguage, changeLanguage } = useAppLocale();
    const [headerScrollHideEnabled, setHeaderScrollHideEnabled] = useRecoilState(
        headerScrollHideEnabledState
    );
    const [passwordForm] = Form.useForm();
    const [languageForm] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    type Step = 'mainProfile' | 'changePassword' | 'changeLanguage';
    const [step, setStep] = useState<Step>('mainProfile');
    const initialLetter = useMemo(() => {
        const name = (profile?.tenNhanVien || '').trim();
        if (!name) return '?';

        const parts = name.split(/\s+/).filter(Boolean);
        const first = parts[0]?.charAt(0) || '';
        const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) || '') : '';
        return (first + last).toUpperCase() || '?';
    }, [profile?.tenNhanVien]);
    const roleGroup = useMemo(() => {
        const roleGroups: React.ReactNode[] = [];
        if (phanHeFlags.isAdmin && phanHeFlags.isQuanLy) {
            roleGroups.push(
                <Tag key="admin" color="blue" icon={<KeyOutlined />}>
                    {t('bsc.label.admin', 'Admin')}
                </Tag>,
                <Tag key="quanly" color="green" icon={<TeamOutlined />}>
                    {t('bsc.label.quanly', 'Quản lý')}
                </Tag>
            );
        } else if (phanHeFlags.isAdmin) {
            roleGroups.push(
                <Tag key="admin" color="blue" icon={<KeyOutlined />}>
                    {t('bsc.label.admin', 'Admin')}
                </Tag>
            );
        } else if (phanHeFlags.isQuanLy) {
            roleGroups.push(
                <Tag key="quanly" color="green" icon={<TeamOutlined />}>
                    {t('bsc.label.quanly', 'Quản lý')}
                </Tag>
            );
        } else {
            roleGroups.push(
                <Tag key="nhanvien" color="cyan" icon={<UserOutlined />}>
                    {t('bsc.label.nhanvien', 'Nhân viên')}
                </Tag>
            );
        }
        return roleGroups;
    }, [phanHeFlags.isAdmin, phanHeFlags.isQuanLy, t]);
    
    useEffect(() => {
        if (!isOpen) {
            setStep('mainProfile');
            passwordForm.resetFields();
            languageForm.resetFields();
        }
    }, [isOpen, passwordForm, languageForm]);

    const handleSubmitChangePassword = async (values: ChangePasswordFormValues) => {
        setSubmitting(true);
        const payload: ChangePasswordPayload = {
            newPassword: values.newPassword,
            oldPassword: values.currentPassword,
            confirmPassword: values.confirmPassword,
        };
        try {
            const response: any = await authApi.changePassword(payload);
            if (response.success) {
                message.success(t('bsc.label.capnhatmatkhauthanhcong', 'Cập nhật mật khẩu thành công'));
                passwordForm.resetFields();
                setStep('mainProfile');
                onClose();
            } else {
                setSubmitting(false);
                message.error(response?.message);
            }
        } catch (error: any) {
            setSubmitting(false);
            message.error(error?.message);
        }
    };

    const handleSubmitChangeLanguage = async (values: ChangeLanguageFormValues) => {
        setSubmitting(true);
        // TODO: Implement call api change language here
        try {
            changeLanguage(values.language);
            message.success(t('bsc.msg.doingonnguthanhcong', 'Đổi ngôn ngữ thành công'));
            setStep('mainProfile');
        } catch {
            message.error(t('bsc.msg.khongthedoingonngu', 'Không thể đổi ngôn ngữ, vui lòng thử lại'));
            setSubmitting(false);
        }
        setSubmitting(false);
    };

    // ====== Profile Main Component ======
    const ProfileMain = () => (
        <div className="flex flex-col gap-8 h-full">
            <div className="flex flex-col items-center text-center gap-3">

                {profile?.avatar ? (
                    <div
                        className="w-[96px] h-[96px] rounded-full p-[6px]"
                        style={{ background: 'linear-gradient(135deg, #FFAB00 0%, #0C68E9 100%)' }}
                    >
                        <Avatar
                            src={profile?.avatar}
                            size={84}
                            icon={<UserOutlined />}
                            className="social-sidebar-avatar"
                        >
                            {initialLetter}
                        </Avatar>
                    </div>
                ) : (
                    <div
                        className="w-[96px] h-[96px] rounded-full p-[6px]"
                        style={{ background: 'linear-gradient(135deg, #FFAB00 0%, #0C68E9 100%)' }}
                    >
                        <div
                            className="w-full h-full rounded-full flex items-center justify-center text-white text-4xl font-semibold"
                            style={{ backgroundColor: CommonJs.genColorByName(profile?.tenNhanVien || '') }}
                        >
                            {initialLetter}
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-1">
                    <div className="text-[16px] font-bold">{profile?.tenNhanVien || t('bsc.label.nguoidung', 'Người dùng')}</div>
                    <div className="text-sm text-gray-500" style={{wordBreak: 'break-all'}}>{email || t('bsc.label.chuacoemail', 'Chưa có email')}</div>
                    {(profile?.tenBoPhan || profile?.tenChucVu) && (
                        <div className="text-sm text-gray-500" style={{ wordBreak: 'break-word' }}>
                            {[profile?.tenBoPhan, profile?.tenChucVu].filter(Boolean).join(' | ')}
                        </div>
                    )}
                    <div className="text-sm text-gray-500 mt-0.5">{roleGroup}</div>
                </div>
            </div>

            <div className="border-t border-dashed border-gray-200 pt-8">
                <div className="text-[11px] text-919EAB uppercase mb-3 tracking-wide font-bold">
                    {t('bsc.label.caidat', 'Cài đặt')}
                </div>

                <div className="flex flex-col gap-1">
                    <div
                        className="flex items-center justify-between px-3 py-3 rounded-lg h-[44px] bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all duration-300"
                        onClick={() => setStep('changePassword')}
                    >
                        <div className="flex items-center gap-3">
                            <ShieldIcon />
                            <span className="font-medium text-637381">{t('bsc.label.thaydoimatkhau', 'Thay đổi Mật khẩu')}</span>
                        </div>
                        <ChevronRight width={15} height={15} />
                    </div>

                    <div
                        className="flex items-center justify-between px-3 py-3 rounded-lg h-[44px] bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all duration-300"
                        onClick={() => setStep('changeLanguage')}
                    >
                        <div className="flex items-center gap-3">
                            <LanguageIcon />
                            <span className="font-medium text-637381">{t('bsc.label.ngonngu', 'Ngôn ngữ')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            {currentLanguage === 'vi'
                                ? t('bsc.option.tiengviet', 'Tiếng Việt')
                                : t('bsc.option.tienganh', 'Tiếng Anh')}
                            <ChevronRight width={15} height={15} />
                        </div>
                    </div>

                    <div
                        className="flex items-center justify-between px-3 py-3 rounded-lg h-[44px] bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all duration-300"
                        onClick={(e) => {
                            if ((e.target as HTMLElement).closest('.ant-switch')) return;
                            const next = !headerScrollHideEnabled;
                            setHeaderScrollHideEnabled(next);
                            try {
                                localStorage.setItem(
                                    HEADER_SCROLL_HIDE_STORAGE_KEY,
                                    JSON.stringify(next)
                                );
                            } catch {
                                // ignore quota / private mode
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            const next = !headerScrollHideEnabled;
                            setHeaderScrollHideEnabled(next);
                            try {
                                localStorage.setItem(
                                    HEADER_SCROLL_HIDE_STORAGE_KEY,
                                    JSON.stringify(next)
                                );
                            } catch {
                                // ignore
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="flex items-center gap-3">
                            <PicCenterOutlined className="text-base text-[#637381]" />
                            <span className="font-medium text-637381">
                                {t('bsc.label.header_autohide_scroll', 'Ẩn header khi cuộn')}
                            </span>
                        </div>
                        <Switch
                            size="small"
                            checked={headerScrollHideEnabled}
                            onChange={(checked) => {
                                setHeaderScrollHideEnabled(checked);
                                try {
                                    localStorage.setItem(
                                        HEADER_SCROLL_HIDE_STORAGE_KEY,
                                        JSON.stringify(checked)
                                    );
                                } catch {
                                    // ignore
                                }
                            }}
                        />
                    </div>
                </div>
            </div>

            <Button
                type="default"
                size="large"
                className="w-full !py-4 !text-[#1B252E] bg-F0F0F0 !font-bold"
                onClick={() => {
                    Modal.confirm({
                        icon: null,
                        footer: null,
                        centered: true,
                        content: (
                            <div className="flex flex-col items-center justify-center gap-5">
                                <div className="text-[16px] font-bold">{t('bsc.label.dangxuat', 'Đăng xuất')}?</div>
                                <div className="text-[16px] text-1B252E">
                                    {t('bsc.msg.muondangxuat', 'Bạn muốn đăng xuất khỏi hệ thống')}
                                </div>
                                <div className="flex items-center justify-center gap-4">
                                    <Button type="default" size="large" onClick={() => Modal.destroyAll()}>
                                        {t('bsc.btn.huy', 'Hủy')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={() => {
                                            Modal.destroyAll();
                                            onClose();
                                            handleLogout();
                                        }}
                                    >
                                        {t('bsc.btn.xacnhan', 'Xác nhận')}
                                    </Button>
                                </div>
                            </div>
                        ),
                    });
                }}
            >
                {t('bsc.label.dangxuat', 'Đăng xuất')}
                <Logout />
            </Button>
        </div>
    );

    return (
        <Drawer
            title={
                <div className="text-1B252E font-bold text-[16px] leading-[39px]">
                    {step === 'mainProfile' ? (
                        t('bsc.label.taikhoan', 'Tài khoản')
                    ) : (
                        <div className="flex items-center justify-start gap-x-4 ">
                            <div
                                onClick={() => setStep('mainProfile')}
                                className="cursor-pointer hover:bg-gray-100 rounded-md p-1 transition-all duration-300"
                            >
                                <ChevronLeft width={24} height={24} />
                            </div>

                            <div className="text-[16px] font-semibold text-1B252E">
                                {step === 'changePassword'
                                    ? t('bsc.label.thaydoimatkhau', 'Thay đổi Mật khẩu')
                                    : t('bsc.label.ngonngu', 'Ngôn ngữ')}
                            </div>
                        </div>
                    )}
                </div>
            }
            open={isOpen}
            onClose={onClose}
            width={320}
            styles={{ mask: { backgroundColor: 'transparent' } }}
            closable={false}
            extra={
                <Button
                    type="text"
                    aria-label="close"
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                    icon={<Close />}
                />
            }
        >
            {step === 'mainProfile' ? (
                ProfileMain()
            ) : step === 'changePassword' ? (
                <ChangePassword form={passwordForm} submitting={submitting} onSubmit={handleSubmitChangePassword} />
            ) : (
                <ChangeLanguage form={languageForm} submitting={submitting} onSubmit={handleSubmitChangeLanguage} />
            )}
        </Drawer>
    );
};
